import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import GlobalSearchState, { GlobalSearchAction, GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import {
  addFlexibleMonthsIfNoneSelected,
  convertDatesToString,
  createSearchSessionKey,
  encodeSearchParams,
  getSearchKey,
  isRoomsInvalid,
} from 'lib/search/searchUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { loadRecentRooms, saveLastDates, saveRecentRooms } from 'components/Search/utils'
import { addGTMEvent } from 'api/googleTagManager'
import { flexibleSearchFormSubmit, searchFormSubmit } from 'analytics/eventDefinitions'
import { useLocation } from 'react-router'
import { pushWithRegion } from 'actions/NavigationActions'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { getQueryParam, parseSearchString } from 'lib/url/searchUrlUtils'
import { queryKeyUrlOfferId, queryKeyUrlOfferType } from 'constants/url'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import usePrevious from 'hooks/usePrevious'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { setSearchEvent, setSearchSession } from 'actions/SearchActions'
import uuidV4 from 'lib/string/uuidV4Utils'
import { useTypeaheadSubmit } from 'hooks/Search/useTypeaheadSubmit'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'

const defaultOccupancies: Array<App.Occupants> = [{ adults: 2 }]

interface Props {
  globalSearchDispatch: Dispatch<GlobalSearchAction>
  globalSearchState: GlobalSearchState
  baseSearchPath?: string
  baseSearchMapPath?: string
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  naturalLanguageSearch?: boolean
  typeaheadTypes?: Array<App.SearchPlaceType>
}

type GlobalSearchVerticalTabBookingHook = Pick<App.GlobalSearchVerticalTabV2, 'onSearch' | 'context'> & {
  updateSearchSessionId: (newSessionId: string) => void
}

// Hook to be used as base for any booking hook (e.g Hotels & Resorts and Villas & Unique Stays)
function useGlobalSearchVerticalTabBooking(props: Props): GlobalSearchVerticalTabBookingHook {
  const {
    globalSearchState,
    globalSearchDispatch,
    baseSearchPath = '/search',
    baseSearchMapPath = '/search/map',
    temporarySearchSubmitRef,
    naturalLanguageSearch = false,
    typeaheadTypes,
  } = props

  const location = useLocation()
  const dispatch = useAppDispatch()
  const [searchSessionId, setSearchSessionId] = useState<string>(uuidV4())
  const isRealTimeDateAndOccupancyExtractionEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.searchRealTimeDateAndOccupancyExtraction)

  const { dispatchTypeaheadExtraction, prepareSearchItem } = useTypeaheadSubmit({
    enabled: naturalLanguageSearch,
    typeaheadTypes,
    isRealTimeDateAndOccupancyExtractionEnabled,
  })

  const updateSearchSessionId = useCallback((newSessionId: string) => {
    setSearchSessionId(newSessionId)
  }, [])

  const saveOccupancies = useCallback(() => {
    const {
      recentOccupancies,
      occupancies = defaultOccupancies,
    } = globalSearchState

    // Filter out item if it's already been saved
    const recentData = recentOccupancies.filter(nextItem => JSON.stringify(nextItem) !== JSON.stringify(occupancies))

    if (recentData.length > 2) recentData.pop()

    recentData.unshift(occupancies)

    globalSearchDispatch({ type: GlobalSearchStateActions.SET_RECENT_OCCUPANCIES, occupancies: recentData })
    saveRecentRooms(recentData)
  }, [globalSearchState, globalSearchDispatch])

  const toggleOccupancyErrors = useCallback((shown: boolean) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown })
  }, [globalSearchDispatch])

  const previousSearchPlaceId = usePrevious(globalSearchState.searchItem?.value)

  const filters = useAppSelector(state => getUrlOfferListFilters(state))

  const generateSearchString = useCallback((globalSearchState: GlobalSearchState): string => {
    const {
      checkinDate,
      checkoutDate,
      flexibleMonths,
      flexibleNights,
      searchItem,
      occupancies = defaultOccupancies,
      isFlexibleDateSelected,
      userSelectedFlexibleMonths,
    } = globalSearchState

    let dates
    if (checkinDate && checkoutDate) {
      dates = convertDatesToString(ISO_DATE_FORMAT, checkinDate, checkoutDate)
    }

    const flexibleMonthsToSearch = addFlexibleMonthsIfNoneSelected(flexibleNights, flexibleMonths)

    return encodeSearchParams({
      urlSearch: location.search,
      searchItem: searchItem ?? ANYWHERE_SEARCH_ITEM,
      dates,
      rooms: occupancies,
      filters: previousSearchPlaceId === searchItem?.value && searchItem?.searchType !== 'bounds' ? filters : undefined,
      isFlexibleWithDate: isFlexibleDateSelected,
      flexibleNights,
      flexibleMonths: flexibleMonthsToSearch,
      userSelectedFlexibleMonths,
    }).toString()
  }, [location.search, previousSearchPlaceId, filters])

  const searchKey = useMemo(() => {
    const searchStr = generateSearchString(globalSearchState)
    return getSearchKey(parseSearchString(searchStr))
  }, [globalSearchState, generateSearchString])

  const onSearch = useCallback(() => {
    const {
      checkinDate,
      checkoutDate,
      flexibleNights,
      occupancies = defaultOccupancies,
      searchItem,
    } = globalSearchState

    if (occupancies.length === 0 || isRoomsInvalid(occupancies)) {
      toggleOccupancyErrors(true)
      return
    }
    saveOccupancies()

    if (checkinDate && checkoutDate) {
      saveLastDates({ checkinDate, checkoutDate })
    }

    // Temporary: Get typeaheadId from ref and dispatch extraction
    const temporaryRefData = temporarySearchSubmitRef?.current ?? null

    // Temporary: Prepare search item with typeaheadId for natural language searches
    const selectedItem = prepareSearchItem(searchItem, ANYWHERE_SEARCH_ITEM, temporaryRefData)

    // Dispatch extraction only if not already completed (checked inside dispatchTypeaheadExtraction)
    dispatchTypeaheadExtraction(temporaryRefData, selectedItem)

    // Update globalSearchState with the selectedItem for generateSearchString
    const stateForSearch = { ...globalSearchState, searchItem: selectedItem }

    const shouldNavigateToMapResults = location.pathname.includes('/map')

    const urlOfferId = getQueryParam(location.search, queryKeyUrlOfferId)
    const urlOfferType = getQueryParam(location.search, queryKeyUrlOfferType)

    const searchStr = generateSearchString(stateForSearch)

    dispatch(setSearchEvent(
      searchKey,
      { searchEvent: { type: 'search_bar' } }),
    )

    dispatch(setSearchSession(
      createSearchSessionKey({
        destinationId: selectedItem?.searchType === 'destination' ? selectedItem.value : undefined,
        propertyId: selectedItem?.searchType === 'property' ? selectedItem.value : undefined,
        landmarkId: selectedItem?.searchType === 'landmark' ? selectedItem.value : undefined,
      }),
      { searchSessionId: searchSessionId },
    ))

    let url = shouldNavigateToMapResults ? baseSearchMapPath : baseSearchPath
    if (urlOfferId && urlOfferType) {
      url = getHotelOfferUrl(urlOfferId, urlOfferType as App.OfferType)
    }
    dispatch(pushWithRegion(url, searchStr))
    addGTMEvent(searchFormSubmit())
    if (flexibleNights) {
      addGTMEvent(flexibleSearchFormSubmit())
    }
  }, [globalSearchState, saveOccupancies, location.pathname, location.search, generateSearchString, dispatch, searchKey, searchSessionId, baseSearchMapPath, baseSearchPath, toggleOccupancyErrors, temporarySearchSubmitRef, dispatchTypeaheadExtraction, prepareSearchItem])

  const setRecentOccupancies = useCallback((occupancies: Array<Array<App.Occupants>>) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_RECENT_OCCUPANCIES, occupancies })
  }, [globalSearchDispatch])

  useEffect(() => {
    setRecentOccupancies(loadRecentRooms())
  }, [setRecentOccupancies])

  return useMemo((): GlobalSearchVerticalTabBookingHook => ({
    context: { dispatch: globalSearchDispatch, state: globalSearchState },
    onSearch,
    updateSearchSessionId,
  }), [globalSearchDispatch, globalSearchState, onSearch, updateSearchSessionId])
}

export default useGlobalSearchVerticalTabBooking
